import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Scale, ArrowUpRight, X } from "lucide-react";
import { api, formatApiError, formatPrice, resolveImageUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { getStoredUtm } from "@/lib/utm";
import { toast } from "sonner";
import YandexPayBadge from "@/components/YandexPayBadge";

export default function ProductCard({ product }) {
  const { toggleWishlist, toggleCompare, isInWishlist, isInCompare } = useShop();
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickForm, setQuickForm] = useState({ customer_name: "", phone: "" });
  const inWish = isInWishlist(product.id);
  const inCmp = isInCompare(product.id);
  const productUrl = `/product/${product.urlSlug || product.slug}`;
  const cardImage = product.card_image || product.images?.[0] || (product.variations || []).find((v) => (v.images || []).length > 0 || v.image)?.images?.[0] || (product.variations || []).find((v) => v.image)?.image;
  const cardTitle = product.card_name || product.name;
  const firstVariationId = (product.variations || [])[0]?.id || null;
  const cardOldPrice = (product.variations || []).find(
    (v) => v.price === (product.min_price ?? product.base_price)
  )?.old_price || product.variations?.[0]?.old_price;

  const submitQuickOrder = async (e) => {
    e.preventDefault();
    if (!quickForm.customer_name.trim() || !quickForm.phone.trim()) return;
    setQuickBusy(true);
    try {
      await api.post("/quick-orders", {
        customer_name: quickForm.customer_name.trim(),
        phone: quickForm.phone.trim(),
        product_id: product.id,
        product_slug: product.slug,
        variation_id: firstVariationId,
        source_page: typeof window !== "undefined" ? window.location.pathname : "/catalog",
        utm: getStoredUtm() || undefined,
      });
      toast.success("Заявка отправлена. Менеджер скоро свяжется с вами.");
      setQuickOpen(false);
      setQuickForm({ customer_name: "", phone: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось отправить заявку"));
    } finally {
      setQuickBusy(false);
    }
  };

  return (
    <>
      <div
        data-testid={`product-card-${product.urlSlug || product.slug}`}
        className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-white transition-transform duration-300 ease-out hover:-translate-y-1 hover:shadow-xl"
      >
        <Link to={productUrl} className="relative block aspect-square overflow-hidden bg-white">
          <img
            src={resolveImageUrl(cardImage)}
            alt={cardTitle}
            className="h-full w-full object-contain p-4 transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
          />
          <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
            {product.is_new && (
              <span className="rounded-full bg-ink px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                Новинка
              </span>
            )}
            {product.variations?.some((v) => v.old_price) && (
              <span className="rounded-full bg-danger px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                Sale
              </span>
            )}
          </div>
          <div className="absolute right-3 top-3 flex flex-col gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <button
              data-testid={`wishlist-toggle-${product.urlSlug || product.slug}`}
              onClick={(e) => {
                e.preventDefault();
                toggleWishlist(product.id);
              }}
              className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
                inWish ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-ink hover:text-white"
              }`}
              aria-label="В избранное"
            >
              <Heart size={15} fill={inWish ? "currentColor" : "none"} />
            </button>
            <button
              data-testid={`compare-toggle-${product.urlSlug || product.slug}`}
              onClick={(e) => {
                e.preventDefault();
                toggleCompare(product);
              }}
              className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
                inCmp ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-ink hover:text-white"
              }`}
              aria-label="К сравнению"
            >
              <Scale size={15} />
            </button>
          </div>
        </Link>

        <div className="flex flex-1 flex-col gap-2 p-5">
          <div>
            <Link to={productUrl} className="block">
              <h3 className="font-semibold tracking-tight text-ink group-hover:underline">
                {cardTitle}
              </h3>
            </Link>
            {product.subtitle && (
              <p className="mt-0.5 line-clamp-1 text-sm text-muted">{product.subtitle}</p>
            )}
          </div>

          <div className="mt-1 flex flex-wrap gap-1.5">
            {Array.from(new Map((product.variations || []).map((v) => [v.color_hex, v])).values())
              .slice(0, 6)
              .map((v) => (
                <span
                  key={v.id}
                  title={v.color}
                  className="h-4 w-4 rounded-full ring-1 ring-line"
                  style={{ background: v.color_hex }}
                />
              ))}
          </div>

          <YandexPayBadge
            amount={product.min_price ?? product.base_price}
            size="s"
            color="grey"
            align="left"
            className="mt-2"
          />

          <div className="mt-auto flex items-end justify-between gap-3 pt-3">
            <div>
              <div className="text-[10px] font-medium text-muted-soft">При оплате наличными</div>
              <div className="flex items-baseline gap-2">
                <div className="text-lg font-extrabold tracking-tight text-ink">
                  {formatPrice(product.min_price ?? product.base_price)}
                </div>
                {cardOldPrice > (product.min_price ?? product.base_price) && (
                  <div className="text-xs text-muted line-through">{formatPrice(cardOldPrice)}</div>
                )}
              </div>
            </div>
            <Link
              to={productUrl}
              data-testid={`open-product-${product.urlSlug || product.slug}`}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-white transition-colors hover:bg-ink-soft"
              aria-label="Открыть"
            >
              <ArrowUpRight size={16} />
            </Link>
          </div>

          <button
            type="button"
            data-testid={`quick-buy-${product.urlSlug || product.slug}`}
            onClick={() => setQuickOpen(true)}
            className="mt-2 w-full rounded-full border border-ink px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
          >
            Купить в 1 клик
          </button>
        </div>
      </div>

      {quickOpen && (
        <div className="fixed inset-0 z-[90] p-4" data-testid="quick-buy-modal">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setQuickOpen(false)}
            aria-label="Закрыть"
          />
          <div className="relative mx-auto mt-[8vh] w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-soft">Быстрый заказ</div>
                <h3 className="mt-1 text-xl font-extrabold tracking-tight text-ink">Купить в 1 клик</h3>
                <p className="mt-1 text-sm text-muted">{cardTitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setQuickOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink hover:bg-black/5"
                aria-label="Закрыть"
              >
                <X size={14} />
              </button>
            </div>

            <form onSubmit={submitQuickOrder} className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">Имя *</span>
                <input
                  required
                  value={quickForm.customer_name}
                  onChange={(e) => setQuickForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">Телефон *</span>
                <input
                  required
                  value={quickForm.phone}
                  onChange={(e) => setQuickForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+7 (___) ___-__-__"
                  className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                />
              </label>

              <button
                type="submit"
                disabled={quickBusy}
                className="w-full rounded-full bg-ink py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
              >
                {quickBusy ? "Отправляем…" : "Отправить заявку"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
